import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { Controller, useFieldArray, useFormContext, useWatch } from 'react-hook-form'
import { X } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'

import type { PaymentFlowFormData } from './payment-flow-form'
import {
    CARD_BRANDS,
    CARD_BRAND_LABELS,
    GROUP_CAPTION_CLASSES,
    GROUP_LABEL_CLASSES,
    isRoutingBrandDisabled,
    parseBinEntries,
    toggleRoutingBrand,
} from './processor-routing'

type Props = {
    processorIndex: number
    pinIndex: number
    isProcessorDisabled: boolean
    supportedCurrencies: string[]
    onRemove: () => void
}

export const RoutingPinRow = ({
    processorIndex,
    pinIndex,
    isProcessorDisabled,
    supportedCurrencies,
    onRemove,
}: Props) => {
    const { t } = useTranslation('paymentFlows')
    const { control } = useFormContext<PaymentFlowFormData>()
    const pinName = `processorConfigs.${processorIndex}.routing.pins.${pinIndex}` as const

    const {
        fields: amountFields,
        append: appendAmount,
        remove: removeAmount,
    } = useFieldArray({
        control,
        name: `${pinName}.amount`,
    })

    const disabledCardBrands =
        useWatch({
            control,
            name: `processorConfigs.${processorIndex}.disabledCardBrands`,
        }) ?? []

    const bins =
        useWatch({
            control,
            name: `${pinName}.bins`,
        }) ?? []

    const [binDraft, setBinDraft] = React.useState(() => bins.join('\n'))
    const [invalidBins, setInvalidBins] = React.useState<string[]>([])

    return (
        <div className="space-y-3 rounded-md border p-3">
            <div className="flex items-start justify-between gap-2">
                <div className="space-y-0.5">
                    <p className={GROUP_LABEL_CLASSES}>
                        {t('form.fields.routing.row_title', { number: pinIndex + 1 })}
                    </p>
                    <p className={GROUP_CAPTION_CLASSES}>{t('form.fields.routing.row_caption')}</p>
                </div>
                <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="size-8 shrink-0"
                    onClick={onRemove}
                    disabled={isProcessorDisabled}
                >
                    <X className="size-4" />
                </Button>
            </div>

            <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">
                    {t('form.fields.routing.amount')}
                </Label>
                {amountFields.map((amountField, amountIndex) => (
                    <div key={amountField.id} className="flex items-center gap-2">
                        <Controller
                            name={`${pinName}.amount.${amountIndex}.min`}
                            control={control}
                            render={({ field, fieldState }) => (
                                <Input
                                    type="number"
                                    min={0}
                                    step={1}
                                    className="h-8"
                                    placeholder={t('form.fields.routing.min')}
                                    value={field.value ?? ''}
                                    onChange={(event) => {
                                        const value = event.target.value
                                        field.onChange(value === '' ? undefined : Number(value))
                                    }}
                                    onBlur={field.onBlur}
                                    disabled={isProcessorDisabled}
                                    error={!!fieldState.error}
                                    aria-label={t('form.fields.routing.min')}
                                />
                            )}
                        />
                        <Controller
                            name={`${pinName}.amount.${amountIndex}.max`}
                            control={control}
                            render={({ field, fieldState }) => (
                                <Input
                                    type="number"
                                    min={0}
                                    step={1}
                                    className="h-8"
                                    placeholder={t('form.fields.routing.max')}
                                    value={field.value ?? ''}
                                    onChange={(event) => {
                                        const value = event.target.value
                                        field.onChange(value === '' ? undefined : Number(value))
                                    }}
                                    onBlur={field.onBlur}
                                    disabled={isProcessorDisabled}
                                    error={!!fieldState.error}
                                    aria-label={t('form.fields.routing.max')}
                                />
                            )}
                        />
                        <Controller
                            name={`${pinName}.amount.${amountIndex}.currency`}
                            control={control}
                            render={({ field }) => {
                                const selected = field.value?.toUpperCase()
                                const options =
                                    selected && !supportedCurrencies.includes(selected)
                                        ? [selected, ...supportedCurrencies]
                                        : supportedCurrencies
                                return (
                                    <select
                                        className="h-8 min-w-[5.5rem] rounded-md border border-input bg-background px-2 text-xs"
                                        value={selected ?? ''}
                                        onChange={(event) => {
                                            const value = event.target.value
                                            field.onChange(value === '' ? undefined : value)
                                        }}
                                        onBlur={field.onBlur}
                                        disabled={isProcessorDisabled}
                                        aria-label={t('form.fields.routing.currency')}
                                    >
                                        <option value="">{t('form.fields.routing.any_currency')}</option>
                                        {options.map((code) => (
                                            <option key={code} value={code}>
                                                {code}
                                            </option>
                                        ))}
                                    </select>
                                )
                            }}
                        />
                        <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="size-8 shrink-0"
                            onClick={() => removeAmount(amountIndex)}
                            disabled={isProcessorDisabled}
                        >
                            <X className="size-3.5" />
                        </Button>
                    </div>
                ))}
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => appendAmount({})}
                    disabled={isProcessorDisabled}
                >
                    {t('form.fields.routing.add_range')}
                </Button>
            </div>

            <Controller
                name={`${pinName}.brands`}
                control={control}
                render={({ field: brandsField }) => {
                    const pinnedBrands = brandsField.value || []
                    return (
                        <div className="space-y-1.5">
                            <Label className="text-xs text-muted-foreground">
                                {t('form.fields.routing.brands')}
                            </Label>
                            <div className="flex flex-wrap gap-1.5">
                                {CARD_BRANDS.map((brand) => {
                                    const isBlocked = isRoutingBrandDisabled(brand, disabledCardBrands)
                                    const isPinned = pinnedBrands.includes(brand)
                                    const chip = (
                                        <button
                                            type="button"
                                            disabled={isBlocked || isProcessorDisabled}
                                            onClick={() =>
                                                brandsField.onChange(
                                                    toggleRoutingBrand(pinnedBrands, brand, disabledCardBrands),
                                                )
                                            }
                                            className={cn(
                                                'rounded-md border px-2 py-0.5 text-[11px] font-medium transition-colors',
                                                isPinned
                                                    ? 'border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300'
                                                    : 'border-muted-foreground/20 text-muted-foreground hover:border-muted-foreground/40',
                                                isBlocked && 'cursor-not-allowed opacity-50 hover:border-muted-foreground/20',
                                            )}
                                        >
                                            {CARD_BRAND_LABELS[brand]}
                                        </button>
                                    )

                                    if (!isBlocked) {
                                        return <React.Fragment key={brand}>{chip}</React.Fragment>
                                    }

                                    return (
                                        <Tooltip key={brand} delayDuration={300}>
                                            <TooltipTrigger asChild>
                                                <span>{chip}</span>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                                {t('form.fields.routing.brand_disabled')}
                                            </TooltipContent>
                                        </Tooltip>
                                    )
                                })}
                            </div>
                        </div>
                    )
                }}
            />

            <Controller
                name={`${pinName}.bins`}
                control={control}
                render={({ field: binsField }) => (
                    <div className="space-y-1.5">
                        <Label className="text-xs text-muted-foreground">
                            {t('form.fields.routing.bins')}
                        </Label>
                        <Textarea
                            className="min-h-[4.5rem] text-sm"
                            value={binDraft}
                            onChange={(event) => setBinDraft(event.target.value)}
                            onBlur={() => {
                                const { entries, invalid } = parseBinEntries(binDraft)
                                setBinDraft(entries.join('\n'))
                                setInvalidBins(invalid)
                                binsField.onChange(entries)
                                binsField.onBlur()
                            }}
                            disabled={isProcessorDisabled}
                            aria-invalid={invalidBins.length > 0}
                        />
                        <p className="text-[10px] text-muted-foreground">
                            {t('form.fields.routing.bins_help')}
                        </p>
                        {invalidBins.length > 0 && (
                            <p className="text-[10px] text-red-600 dark:text-red-400">
                                {t('validation.routingBin')}
                                {': '}
                                {invalidBins.join(', ')}
                            </p>
                        )}
                    </div>
                )}
            />
        </div>
    )
}
